'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';
import { addDays } from 'date-fns';
import { UserImpressionType } from '@trucksmarter/shared';
import { useImpression } from '../shared/ImpressionsProvider/ImpressionsProvider';
import { LOAD_BOARD_PAGE_PATH } from '../../pages';
import DallasSvgIconFeedbackOutlined from '../Dallas/Icons/DallasSvgIconFeedbackOutlined';
import NavButton from '../../(authenticated)/_ui/NavItems/NavButton';
import FeedbackDialog from './FeedbackDialog';

const REQUEST_FEEDBACK_TIMEOUT_MS = 3_000;
const MIN_DAYS_SINCE_FEEDBACK_IMPRESSION = 7;
const MAX_DAYS_SINCE_FEEDBACK_IMPRESSION = 14;

export default function FeedbackNavButton() {
  const pathname = usePathname();
  const buttonRef = useRef(null);
  const timeoutRef = useRef<NodeJS.Timeout>(null);
  const { data: feedbackSubmitImpression } = useImpression(
    UserImpressionType.WebAppFeedbackSubmitted,
  );
  const {
    data: feedbackRequestImpression,
    bumpViewCount: bumpFeedbackRequestImpressionViewCount,
  } = useImpression(UserImpressionType.WebAppFeedbackRequested);
  const [dialog, setDialog] = useState<React.ReactElement | null>(null);
  const openFeedbackDialog = useCallback(
    () => setDialog(<FeedbackDialog onClose={() => setDialog(null)} />),
    [],
  );

  useEffect(() => {
    if (timeoutRef.current != null) {
      clearTimeout(timeoutRef.current);
    }

    const lastFeedbackImpressionDates = (
      [
        feedbackSubmitImpression?.lastUpdatedAt,
        feedbackRequestImpression?.lastUpdatedAt,
      ].filter((lastUpdatedAt) => !!lastUpdatedAt) as string[]
    ).map((lastUpdatedAt) => new Date(lastUpdatedAt));
    const lastFeedbackImpressionDate = lastFeedbackImpressionDates.length
      ? new Date(
          Math.max(
            ...lastFeedbackImpressionDates.map((date) => date.getTime()),
          ),
        )
      : null;
    const nextFeedbackRequestDate =
      lastFeedbackImpressionDate != null
        ? addDays(
            lastFeedbackImpressionDate,
            Math.ceil(
              Math.random() *
                (MAX_DAYS_SINCE_FEEDBACK_IMPRESSION -
                  MIN_DAYS_SINCE_FEEDBACK_IMPRESSION),
            ) + MIN_DAYS_SINCE_FEEDBACK_IMPRESSION,
          )
        : null;

    if (
      pathname === LOAD_BOARD_PAGE_PATH &&
      (nextFeedbackRequestDate == null ||
        new Date().getTime() >= nextFeedbackRequestDate.getTime())
    ) {
      timeoutRef.current = setTimeout(() => {
        bumpFeedbackRequestImpressionViewCount();
        openFeedbackDialog();
      }, REQUEST_FEEDBACK_TIMEOUT_MS);
    }
  }, [
    pathname,
    openFeedbackDialog,
    feedbackSubmitImpression,
    feedbackRequestImpression,
    bumpFeedbackRequestImpressionViewCount,
  ]);

  return (
    <>
      <NavButton
        ref={buttonRef}
        icon={<DallasSvgIconFeedbackOutlined />}
        onClick={openFeedbackDialog}
        label="Give Feedback"
      />
      {dialog}
    </>
  );
}
